import React, { useState, useMemo } from "react";
/* eslint-disable no-nested-ternary */
import styled from "styled-components";
import { GrFormClose } from "react-icons/gr";
import { useListarConsolidados } from "services/api/Common_Produtos/listar-consolidados";
import { useRegionaisVendas } from "services/api/DadosComum/regionais-vendas";
import InputG from "components/InputG";
import SelectAutoCompleteG from "components/selectAutoCompleteG";

interface Props {
  onClose: () => void;
}

const ModalResumo: React.FC<Props> = ({ onClose }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedRegional, setSelectedRegional] = useState<any>(null);

  const { data: regionaisData } = useRegionaisVendas();
  const regionaisVendas = regionaisData?.data || [];

  const regionaisOptions = useMemo(() => {
    return regionaisVendas.map((item: any) => ({
      label: item?.name || item?.nome || "",
      value: item?.id || "",
    }));
  }, [regionaisVendas]);

  const { data, isLoading } = useListarConsolidados({
    queryKey: "listar-consolidados",
    enabled: !!selectedRegional?.value,
    idRegionalVendas: selectedRegional?.value,
  });

  const consolidados = data?.data || [];

  const removeAccents = (str: string) => {
    if (!str) return "";
    return str
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase();
  };

  const filteredConsolidados = useMemo(() => {
    if (!searchTerm.trim()) {
      return consolidados;
    }

    const term = removeAccents(searchTerm.trim());

    return consolidados.filter((item: any) => {
      const consolidado = item?.consolidado;
      const empreendimento = consolidado?.empreendimento;

      const codigo = removeAccents(empreendimento?.codigoSap?.toString() || "");
      const nomeEmpreendimento = removeAccents(
        empreendimento?.nome?.toString() || ""
      );

      return codigo.includes(term) || nomeEmpreendimento.includes(term);
    });
  }, [consolidados, searchTerm]);

  const formatNumber = (num: number) => {
    return num?.toLocaleString("pt-BR") || "0";
  };

  return (
    <ModalContainer>
      <ModalHeader>
        <ModalTitle>Resumo</ModalTitle>
        <CloseButton onClick={onClose}>
          <GrFormClose size={24} />
        </CloseButton>
      </ModalHeader>
      <ContentModal>
        <FiltersContainer>
          <SelectWrapper>
            <SelectAutoCompleteG
              value={selectedRegional}
              onChange={(newValue: any) => setSelectedRegional(newValue)}
              data={regionaisOptions}
              placeholder="Selecione a regional de vendas"
            />
          </SelectWrapper>
          <InputG
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Pesquisar por código ou empreendimento..."
            width="100%"
            disabled={!selectedRegional}
          />
        </FiltersContainer>
        <TableContainer>
          <TableWrapper>
            <StyledTable>
              <thead>
                <tr>
                  <th>Código</th>
                  <th>Empreendimento</th>
                  <th>TOTAL UNID.</th>
                  <th>VEND</th>
                  <th>PV</th>
                  <th>CRE</th>
                  <th>JUR</th>
                  <th>BLQ</th>
                  <th>EST</th>
                </tr>
              </thead>
              <BodyScroll>
                {!selectedRegional ? (
                  <tr>
                    <td
                      colSpan={9}
                      style={{ textAlign: "center", padding: "20px" }}
                    >
                      Por favor, selecione uma regional de vendas para
                      visualizar os dados
                    </td>
                  </tr>
                ) : isLoading ? (
                  <tr>
                    <td
                      colSpan={9}
                      style={{ textAlign: "center", padding: "20px" }}
                    >
                      Carregando...
                    </td>
                  </tr>
                ) : filteredConsolidados.length === 0 ? (
                  <tr>
                    <td
                      colSpan={9}
                      style={{ textAlign: "center", padding: "20px" }}
                    >
                      {searchTerm.trim()
                        ? "Nenhum resultado encontrado para a pesquisa"
                        : "Nenhum consolidado encontrado"}
                    </td>
                  </tr>
                ) : (
                  filteredConsolidados.map((item: any, idx: number) => {
                    const consolidado = item?.consolidado;
                    const empreendimento = consolidado?.empreendimento;
                    return (
                      <tr key={empreendimento?.id || idx}>
                        <td>{empreendimento?.codigoSap || "-"}</td>
                        <td>{empreendimento?.nome || "-"}</td>
                        <td>{formatNumber(consolidado?.totalUnidades)}</td>
                        <td>{formatNumber(consolidado?.totalVendas)}</td>
                        <td>{formatNumber(consolidado?.totalPv)}</td>
                        <td>{formatNumber(consolidado?.totalCre)}</td>
                        <td>{formatNumber(consolidado?.totalJur)}</td>

                        <td>{formatNumber(consolidado?.totalBlq)}</td>
                        <td>{formatNumber(consolidado?.totalEstoque)}</td>
                      </tr>
                    );
                  })
                )}
              </BodyScroll>
            </StyledTable>
          </TableWrapper>
        </TableContainer>
      </ContentModal>
    </ModalContainer>
  );
};

export default ModalResumo;

const ModalContainer = styled.div`
  display: flex;
  flex-direction: column;
  background: white;
  border-radius: 8px;
  overflow: hidden;
  width: 85vw;
  max-width: 1500px;
  max-height: 85vh;
  margin: 0 auto;
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 24px 24px 16px 24px;
  border-bottom: 1px solid #e5e7eb;
`;

const ModalTitle = styled.h2`
  font-family: Inter;
  font-size: 18px;
  font-weight: 500;
  color: #111827;
  margin: 0;
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: background-color 0.2s;

  &:hover {
    background-color: #f3f4f6;
  }
`;

const ContentModal = styled.div`
  padding: 24px;
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const FiltersContainer = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 16px;
  align-items: flex-start;
`;

const SelectWrapper = styled.div`
  min-width: 300px;
  flex-shrink: 0;
`;

const TableContainer = styled.div`
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
`;

const TableWrapper = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  min-height: 0;

  /* WebKit scrollbar */
  &::-webkit-scrollbar {
    width: 8px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: #2d5bff;
    border-radius: 8px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: #2d5bff;
  }

  /* Firefox */
  scrollbar-width: thin;
  scrollbar-color: #2d5bff transparent;
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;

  thead th {
    text-align: left;
    font-family: Inter;
    font-size: 13px;
    font-weight: 600;
    color: #667085;
    background: #f8fafc;
    padding: 14px 16px;
    border-bottom: 1px solid #e5e7eb;
    position: sticky;
    top: 0;
    z-index: 2;
  }

  thead th:nth-child(n + 3) {
    text-align: right;
  }

  thead th:first-child {
    border-top-left-radius: 12px;
  }
  thead th:last-child {
    border-top-right-radius: 12px;
  }

  tbody td {
    padding: 14px 16px;
    font-family: Inter;
    font-size: 13px;
    color: #111827;
    border-top: 1px solid #e5e7eb;
    background: #ffffff;
  }

  tbody td:nth-child(n + 3) {
    text-align: right;
  }

  tbody tr:first-child td {
    border-top: none;
  }
`;

const BodyScroll = styled.tbody``;
